import {
  ChangeEvent,
  Dispatch,
  MouseEvent,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import queryHooks from '../../common/queryHooks/index.ts';
import M from 'materialize-css';
import { HydratedTradingPartner } from '../../common/types/compoundModels.ts';
import { ContactType, TradingPartnerStatusEnum } from '../../common/enums.ts';
import { Link } from 'react-router-dom';
import {
  TradingPartnerAddress,
  TradingPartnerContact,
} from '../../common/types/models.ts';
import {
  SendPedigreeData,
  sendPedigreeFinalReviewData,
} from './SendPedigree.tsx';
import React from 'react';

interface IProps {
  setSendData: Dispatch<SetStateAction<SendPedigreeData>>;
  setFinalReviewData: Dispatch<SetStateAction<sendPedigreeFinalReviewData>>;
}

const TradingPartnerSelect = ({ setSendData, setFinalReviewData }: IProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const [tradingPartner, setTradingPartner] =
    useState<HydratedTradingPartner>();
  const [activeTradingPartners, setActiveTradingPartners] = useState<
    HydratedTradingPartner[]
  >([]);
  const [tradingPartnerContactsWithEmail, setTradingPartnerContactsWithEmail] =
    useState<TradingPartnerContact[]>([]);
  const [selectedEmailAddresses, setSelectedEmailAddresses] = useState<
    string[]
  >([]);
  const [selectedTradingPartnerAddress, setSelectedTradingPartnerAddress] =
    useState<TradingPartnerAddress>();

  const { data: tradingPartners, isLoading: isLoadingTradingPartners } =
    queryHooks.tradingPartner.useReadAllByUserId();

  const { data: verifiedTradingPartnerEligibility } =
    queryHooks.verifiedTradingPartner.useReadOneIsAs2ActiveById(
      tradingPartner?.verifiedTradingPartnerId
    );

  useLayoutEffect(() => {
    const { current } = modalRef;
    if (!current) return;

    M.Modal.init(current);
    return () => {
      M.Modal.getInstance(current)?.destroy();
    };
  }, []);

  const getModal = () =>
    modalRef.current ? M.Modal.getInstance(modalRef.current) : null;

  useEffect(() => {
    if (!tradingPartners) return;
    const filtered = tradingPartners?.filter(
      ({ statuses, isActive }) =>
        statuses.sort(
          (a, b) =>
            new Date(b.dateCreated).getTime() -
            new Date(a.dateCreated).getTime()
        )[0].status === TradingPartnerStatusEnum.active && isActive
    );

    setActiveTradingPartners([...filtered]);
  }, [tradingPartners]);

  useEffect(() => {
    M.FormSelect.init(document.querySelectorAll('.sendPedigreeTpSelect'));
  }, [activeTradingPartners, tradingPartner]);

  useEffect(() => {}, [tradingPartner]);

  const onChange = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const tp = tradingPartners?.find(({ id }) => id === parseInt(value));
    const address = tp?.addresses?.[0];
    setTradingPartner(tp);
    setSelectedTradingPartnerAddress(address);
    setFinalReviewData(prev => ({
      ...prev,
      tradingPartner: tp,
      tradingPartnerAddress: address,
    }));
  };

  useEffect(() => {
    if (tradingPartner) {
      const filtered = tradingPartner.contacts?.filter(({ contactDetails }) =>
        contactDetails?.some(
          ({ contactType }) => contactType === ContactType.email
        )
      );

      if (filtered) setTradingPartnerContactsWithEmail([...filtered]);
    }
  }, [tradingPartner]);

  useLayoutEffect(() => {
    M.FormSelect.init(document.querySelectorAll('#selectedEmailAddresses'));
  }, [tradingPartnerContactsWithEmail]);

  const onSelectedEmailAddressChange = ({
    target,
  }: ChangeEvent<HTMLSelectElement>) => {
    if (!target) return;

    const select = M.FormSelect.getInstance(target);
    setSelectedEmailAddresses(select?.getSelectedValues() || []);
  };

  useEffect(() => {
    setSendData({
      tradingPartnerAddressId: selectedTradingPartnerAddress?.id,
      verifiedTradingPartnerEligibility,
      selectedEmailAddresses,
    });
  }, [
    selectedTradingPartnerAddress,
    verifiedTradingPartnerEligibility,
    selectedEmailAddresses,
    setSendData,
  ]);

  const openChangeAddressModal = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    getModal()?.open();
  };

  return (
    <div className="card">
      <div className="card-content">
        <div className="row">
          <h6>Send To</h6>
          <div className="divider" />
        </div>
        <div className="row">
          <div className="input-field col s12 m4">
            <select
              id="tradingPartnerId"
              className="sendPedigreeTpSelect"
              value={tradingPartner?.id}
              onChange={onChange}
              disabled={
                isLoadingTradingPartners || !activeTradingPartners?.length
              }
            >
              <option value="">Select A Trading Partner</option>
              {activeTradingPartners?.map(tp => (
                <option key={tp.id} value={tp.id}>
                  {tp.name}
                </option>
              ))}
            </select>
          </div>
          {!isLoadingTradingPartners && !activeTradingPartners?.length ? (
            <div className="input-field col s12 m4 offset-m2">
              <span>
                You have no active trading partners. Add one
                <Link to="/tradingpartner"> here</Link>.
              </span>
            </div>
          ) : (
            <div className="input-field col s12 m4 offset-m2">
              {selectedTradingPartnerAddress ? (
                <>
                  <p>{selectedTradingPartnerAddress.addressLineOne}</p>
                  <p>{selectedTradingPartnerAddress.addressLineTwo}</p>
                  <p>
                    {selectedTradingPartnerAddress.city},{' '}
                    {selectedTradingPartnerAddress.state?.code}
                  </p>
                </>
              ) : tradingPartner ? (
                <span>
                  This trading partner has no address. Add one{' '}
                  <Link to={`/tradingpartner/${tradingPartner?.id}`}>here</Link>
                  .
                </span>
              ) : null}
            </div>
          )}
          {tradingPartner?.addresses?.length &&
            tradingPartner.addresses.length > 1 && (
              <a href="/" onClick={openChangeAddressModal}>
                Select a different address
              </a>
            )}
        </div>
        <div className="row">
          {tradingPartner &&
            selectedTradingPartnerAddress &&
            !verifiedTradingPartnerEligibility?.isAs2Active && (
              <>
                {tradingPartnerContactsWithEmail.length ? (
                  <div className="input-field col s12">
                    <select
                      id="selectedEmailAddresses"
                      className="sendPedigreeTpSelect"
                      onChange={onSelectedEmailAddressChange}
                      multiple
                    >
                      {tradingPartnerContactsWithEmail?.map(
                        ({ firstName, lastName, contactDetails }) =>
                          contactDetails?.map(
                            ({ id, contactType, contactDetail }) =>
                              contactType === ContactType.email ? (
                                <option key={id} value={contactDetail}>
                                  {firstName} {lastName} - {contactDetail}
                                </option>
                              ) : null
                          )
                      )}
                    </select>
                    <label htmlFor="selectedEmailAddresses">
                      Email Address(es)
                    </label>
                  </div>
                ) : null}
              </>
            )}
        </div>
        <div className="row">
          {tradingPartner &&
            !verifiedTradingPartnerEligibility?.isAs2Active && (
              <>
                <p>
                  This trading partner does not have an active AS2 connection.
                </p>
                {tradingPartner.contacts?.some(contact =>
                  contact.contactDetails?.some(
                    detail => detail.contactType === ContactType.email
                  )
                ) ? (
                  <p>
                    The trading partner will receive an email with a link to
                    download the required epcis directly from MedTrax.com
                  </p>
                ) : (
                  <p>
                    This trading partner also lacks a contact with an email
                    address for us to send a link to. Please add one{' '}
                    <Link to={`/tradingpartner/${tradingPartner.id}`}>
                      here
                    </Link>
                    .
                  </p>
                )}
              </>
            )}
        </div>
        {tradingPartner &&
          !verifiedTradingPartnerEligibility?.hasActiveGlobalLocationNumber && (
            <div className="row">
              <span className="red-text">
                This trading partner does not have a verified global location
                number. Please contact your MedTrax representative.
              </span>
            </div>
          )}
      </div>
      <div ref={modalRef} className="modal">
        <div className="modal-content">
          <div className="row">
            <h6>Select an address</h6>
            <div className="divider" />
          </div>
          <div className="row">
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {tradingPartner?.addresses?.map(address => {
                const {
                  id,
                  addressLineOne,
                  addressLineTwo,
                  city,
                  state: { code },
                } = address;

                return (
                  <div
                    key={id}
                    className="card"
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setSelectedTradingPartnerAddress(address);
                      setFinalReviewData(prev => ({
                        ...prev,
                        tradingPartnerAddress: address,
                      }));
                      getModal()?.close();
                    }}
                  >
                    <div className="card-content">
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <span>{addressLineOne}</span>
                          {id === selectedTradingPartnerAddress?.id && (
                            <span className="orange-text">selected</span>
                          )}
                        </div>
                        <span>{addressLineTwo}</span>
                        <span>
                          {city}, {code}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingPartnerSelect;
